import { LoggedUpload } from './document-types'
import { FundReleaseType, FundingSchedule } from './loan-application'
import { getLoanAppChecklistItemStatus } from "./loan-application-phase"
import { getLoanFundingChecklistItemDate, getLoanFundingChecklistItemStatus } from "./loan-funding-phase"
import { StatusData } from './status-data'
import { Document } from 'mongoose'

export enum ChecklistItemType {
  System = 'system',
  DocumentUpload = 'document upload',
  Airtable = 'airtable',
  FundingRequest = 'funding request',
}

export enum ChecklistItemSubType {
  Internal = 'internal',
  SolarMonitoring = 'solar monitoring',
  InspectionRequest = 'inspection request',
}

export enum ChecklistPhase {
  LoanApplication = 'loan-application',
  LoanFunding = 'loan-funding',
}

export enum ChecklistOwner {
  Installer = 'installer',
  Customer = 'customer',
  Bank = 'bank'
}

export enum ChecklistItemStatus {
  InProgress = 'in progress',
  NeedsAction = 'needs action',
  Completed = 'completed'
}

export const DISBURSED_LABEL_TO_FIELD_MAP: Record<string, string> = {
  'Roof Funding Disbursed': 'roofFundingDate',
  'Initial Funding Disbursed': 'partialFundingDate',
  'Final Funding Disbursed': 'fullFundingDate',
  'Full Funding Disbursed': 'fullFundingDate',
}

export const BANKER_COMMENT_FIELDS = [
  'contractNotes',
  'loanDocumentsSentNotes',
  'loanDocumentsSignedNotes',
  'initialBankReviewNotes',
  'homeOwnershipVerificationNotes',
  'bankFinalReviewNotes',
  'roofPermittingComments',
  'passedRoofInspectionComments',
  'roofLienWaiverFormComments',
  'constructionPermitComments',
  'engineeringPlansComments',
  'proofOfPassedInspectionComments',
  'proofOfPermissionToOperateComments',
  'contractorLienWaiverFormComments',
  'roofFundReleaseRequestComments',
  'roofFundingDisbursedComments',
  'initialFundReleaseRequestComments',
  'initialFundingDisbursedComments',
  'finalFundingReleaseRequestComments',
  'finalFundingDisbursedComments',
  'fullFundingReleaseRequestComments'
]

export interface IChecklistItem extends Document {
  label: string
  description: string
  type: ChecklistItemType
  subType?: ChecklistItemSubType
  phase: ChecklistPhase
  owner: ChecklistOwner
  dataField: string
  fundReleaseTypes?: FundReleaseType[]
  fundingSchedules?: FundingSchedule[]
  active?: boolean
  createdDate?: Date
  updatedDate?: Date
  agentWorkflowId?: string,
  agentWorkflowEnabled?: boolean,
  presubmitAgentWorkflowId?: string,
  sequence: number,
}

export const isSolarMonitoringChecklistItem = (
  item: Pick<IChecklistItem, 'type' | 'subType'>,
): boolean => (
  item.type === ChecklistItemType.System &&
  item.subType === ChecklistItemSubType.SolarMonitoring
)

export const isInspectionRequestChecklistItem = (
  item: Pick<IChecklistItem, 'type' | 'subType'>,
): boolean => (
  item.type === ChecklistItemType.System &&
  item.subType === ChecklistItemSubType.InspectionRequest
)

export const getChecklistItemTypeLabel = (
  item: Pick<IChecklistItem, 'type' | 'subType'>,
): string => (
  item.subType ? `${item.type} / ${item.subType}` : item.type
)

export const getSystemChecklistTypeChangeError = (
  existingType: ChecklistItemType | undefined,
  nextType: unknown,
): string | undefined => {
  if (existingType !== ChecklistItemType.System) return undefined
  if (nextType === undefined || nextType === ChecklistItemType.System) return undefined
  return 'system checklist items cannot change type'
}

export interface FundRelaseGroup {
  fundReleaseType: FundReleaseType;
  checklistItems: IChecklistItem[];
}

export const getBadgeClass = (status: string) => {
  switch (status) {
    case ChecklistItemStatus.Completed:
      return 'text-bg-success'
    case ChecklistItemStatus.NeedsAction:
      return 'text-bg-warning'
    case ChecklistItemStatus.InProgress:
      return 'text-bg-info'
    default:
      return ''
  }
}

export const getLatestDocTimestamp = (uploads: LoggedUpload[] = [], docType: string): Date | undefined => {
  let latest: Date | undefined

  if (!Array.isArray(uploads)) return undefined

  for (const upload of uploads) {
    if (!upload || !Array.isArray(upload.documents)) continue
    const ts = new Date(upload.timestamp)
    if (isNaN(ts.getTime())) continue

    for (const doc of upload.documents) {
      if (doc.docType === docType) {
        if (!latest || ts > latest) {
          latest = ts
        }
      }
    }
  }

  return latest
}

export const getChecklistItemDate = (item: IChecklistItem, project: StatusData) => {
  if (item.type === ChecklistItemType.DocumentUpload) {
    return getLatestDocTimestamp(project.uploads, item.label)
  }

  if (
    item.type === ChecklistItemType.FundingRequest ||
    isInspectionRequestChecklistItem(item)
  ) {
    return getLoanFundingChecklistItemDate(item, project)
  }

  // Special handling for Customer Prequalification Completed
  // 1. First try the airtable field (prequalTimestamp)
  // 2. Check if preapproval letter agreement exists and is agreed to
  // 3. Check if project is prequalified (even without a specific date)
  if (item.label === 'Customer Prequalification Completed') {
    if (project[item.dataField]) {
      return project[item.dataField]
    }
    if (project.preapprovalLetter?.agreement?.agrees) {
      return project.preapprovalLetter.agreement.timestamp
    }
    if (project.isPrequalified) {
      return null
    }
    return undefined
  }

  return project[item.dataField]
}

/**
 * Single source of truth for deciding whether a checklist value counts as present.
 * Values reach the UI as Dates, ISO strings, or Airtable date strings depending on
 * whether they came from Mongo, JSON, or a sync, so all of those must behave the same.
 */
export const hasChecklistValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (value instanceof Date) return !isNaN(value.getTime())
  if (typeof value === 'string') return value.trim().length > 0
  return Boolean(value)
}

export const getStatusByBankerComments = (
  project: StatusData,
  field: string
): ChecklistItemStatus | undefined => {
  if (!BANKER_COMMENT_FIELDS.includes(field)) return undefined

  return hasChecklistValue(project[field]) ? ChecklistItemStatus.NeedsAction : undefined
}

export const getActionRequiredBy = (checklistItems: IChecklistItem[], project: any): ChecklistOwner[] | undefined => {
  if (!checklistItems || checklistItems.length === 0) return undefined

  // Determine which phase to process based on project state
  const targetPhase = project.nTPGiven ? ChecklistPhase.LoanFunding : ChecklistPhase.LoanApplication

  const ownersWithActionRequired = checklistItems
    .filter(item => item.phase === targetPhase)
    .filter(item => (
      !isSolarMonitoringChecklistItem(item) || Boolean(project.monitoringSiteId)
    ))
    .filter(item => {
      const status = project.nTPGiven
        ? getLoanFundingChecklistItemStatus(item, project)
        : getLoanAppChecklistItemStatus(item, project)

      return status === ChecklistItemStatus.NeedsAction || status === ChecklistItemStatus.InProgress
    })
    .map(item => item.owner)

  const uniqueOwners = Array.from(new Set(ownersWithActionRequired))
  return uniqueOwners.length > 0 ? uniqueOwners : undefined
}

export const getDisbursedDateFromLabel = (label: string, project: any): Date | undefined => {
  const field = DISBURSED_LABEL_TO_FIELD_MAP[label]
  return field ? project?.[field] : undefined
}

export const getDisplayLabel = (label: string) => {
  return `${label} Submitted`
}
